import { dateToYMDformat, getContractRange, toLongDate, YEAR } from 'utils/date'
import DatePickerPopup from 'components/pickers/DatePickerPopup'
import React, { useEffect, useRef, useState } from 'react'
import { PickerLimits } from './useOrderDateLimits'
import Checkbox from 'components/inputs/Checkbox'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import useDevice from 'hooks/useDevice'
import { v7 } from 'uuid'

interface Range {
  m: number
  d: number
}

interface RentalPeriodSelectProps {
  disabled?: boolean
  minusOneDay?: boolean
  label?: string
  start: string
  m: number
  d: number
  date: string
  minDate: string
  maxDate?: string
  /** miekkie/twarde granice kalendarza (nadpisuja minDate/maxDate tylko w kalendarzu, nie w liscie okresow) */
  calendarLimits?: PickerLimits
  setDate: (date: string) => void
  setRange: (range: Range) => void
}

const RentalPeriodSelect: React.FC<RentalPeriodSelectProps> = ({ disabled, minusOneDay = false, label, start, date, m, d, minDate, maxDate, calendarLimits, setDate, setRange }) => {
  const device = useDevice()
  const mobile = device.isMobile
  const [expanded, expand] = useState(false)
  const [reversed, setReverse] = useState(false)
  const { popup, openPopup } = usePopupState<{ type: 'rentalPeriodCalendar' }>()
  const range = maxDate && start
    ? getContractRange(maxDate, start, minusOneDay)
    : undefined
  const id = v7()

  const containerRef = useRef<HTMLDivElement>(null)
  const optionsRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation('calendar')
  const checkIsReversed = () => {
    const container = containerRef.current
    if (!container) return
    const dimensions = container.getBoundingClientRect()
    setReverse(dimensions.y > (window.innerHeight / 2))
  }

  useEffect(() => {
    checkIsReversed()
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (container && expanded) {
      const unfocus = (event: Event) => {
        if (!container.contains(event.target as Node)) {
          expand(false)
        }
      }

      const root = document.querySelector('#root')
      root?.addEventListener('click', unfocus)
      checkIsReversed()
      return () => {
        root?.removeEventListener('click', unfocus)
      }
    }
  }, [expanded])

  return (
    <>
      {popup?.type === 'rentalPeriodCalendar' && <DatePickerPopup
        value={date}
        minDate={minDate}
        maxDate={maxDate || dateToYMDformat(new Date(Date.now() + (5 * YEAR)))}
        {...calendarLimits}
        onChange={setDate}
      />}
      <div
        ref={containerRef}
        className={`select__container${expanded ? ' --expanded' : ''}${mobile ? ' --mobile' : ''}${reversed ? ' --reverse' : ''}`}
        style={disabled ? { opacity: 0.5 } : undefined}>
        <div className='input__default'>
          {label && <label className='input__label' htmlFor={id}>
            <span>{label}</span>
          </label>}

          <div className='input__main'>
            <div
              id={id}
              className='select'
              onClick={() => {
                if (disabled) return
                expand(e => !e)
              }}>
              <span>
                {(d && date) ? toLongDate(date, undefined, true)
                  : m === -1 ? t('indefinitePeriod')
                    : m > 0 ? `${m} ${t(m === 1 ? 'month' : m < 5 ? 'month<5' : 'month>=5')}`
                      : ''}
              </span>
              <i className={`icon icon--chevron-${reversed ? expanded ? 'down' : 'up' : expanded ? 'up' : 'down'}`} />
            </div>
          </div>
        </div>

        <div
          ref={optionsRef}
          className='select__options'>
          {!maxDate && <div className='select__option'>
            <Checkbox
              label={t('indefinitePeriod')}
              name={t('indefinitePeriod')}
              checked={m === -1}
              changeHandler={() => {
                expand(false)
                setRange({ m: -1, d: 0 })
              }}
            />
          </div>}
          {Array.from(Array(range ? range.m : 12)).map((_, idx) => {
            const index = idx + 1
            const isSelected = d === 0 && m === index
            const name = `${index} ${t(index === 1 ? 'month' : index < 5 ? 'month<5' : 'month>=5')}`
            return <div
              key={idx}
              className='select__option'>
              <Checkbox
                label={name}
                name={name}
                checked={isSelected}
                changeHandler={() => {
                  expand(false)
                  setRange({ m: index, d: 0 })
                }}
              />
            </div>
          })}
          <div className='select__option --bottom'>
            <span
              onClick={() => {
                expand(false)
                openPopup({ type: 'rentalPeriodCalendar' })
              }}>
              {t('selectFromTheCalendar')}
            </span>
          </div>
        </div>
      </div>
    </>
  )
}

export default RentalPeriodSelect